import { attributesIO } from '@type-r/endpoints';
import { AttributesMixin, Store as BaseStore, define } from '@type-r/models';
import { AssetTypesLookup, CurrenciesLookup, GoverningLawsLookup } from 'src/app/pages/digitization/api/lookups';
import { InternalInvoiceStatus } from './commonModels';
import { CommunicationModel } from './communication';
import { CompanyModel, Conversation, Participant, PlatformCompany, RelationshipModel, Supplier, WorkflowMapModel } from './relationship';
import { getPlatformCode } from 'src/app/pages/digitization/api/endpoint';

export interface Store extends AttributesMixin<typeof Store> { }
@define
export class Store extends BaseStore {
    static endpoint = attributesIO();
    static attributes = {
        conversations: [Conversation],
        relationships: [RelationshipModel],
        communications: [CommunicationModel],
        workflows: [WorkflowMapModel],
        companies: [CompanyModel],
        internalInvoiceStatuses: [InternalInvoiceStatus],
        suppliers: [Supplier],
        hasLoadedData: Boolean,
        governingLawsLookup: GoverningLawsLookup,
        currenciesLookup: CurrenciesLookup,
        assetTypesLookup: AssetTypesLookup,
        platformCompanies: [PlatformCompany]
    };

    isLoading() {
        var t1 = this.conversations.hasPendingIO();
        var t2 = this.relationships.hasPendingIO();
        var t3 = this.workflows.hasPendingIO();
        var t4 = this.companies.hasPendingIO();
        var t5 = this.internalInvoiceStatuses.hasPendingIO();
        var t6 = this.suppliers.hasPendingIO();
        return t1 || t2 || t3 || t4 || t5 || t6;
    }

    async fetchAfterLogin(forceReload?) {

        if (this.isLoading() || (this.hasLoadedData && !forceReload)) return;
        this.hasLoadedData = false;

        return Promise.all([
            this.suppliers.fetch({ params: { productCode: 'SCF'}, 
                                   headers: {"X-Platform-Code": getPlatformCode() }}),
            this.platformCompanies.fetch({headers: {"X-Platform-Code": getPlatformCode() }})
            ])
            .then(() => {
                this.hasLoadedData = true;
                console.log('fetchAfterLogin -> done');
            })
            .catch(e => {
                console.log('fetchAfterLogin ->', e);
                this.hasLoadedData = false;
            });
    }

    relationshipsByCompanies(supplier, customer) {
        return this.relationships.filter(x =>
            x.Supplier && x.Customer &&
            x.Supplier.companyId === Number(supplier.id) &&
            x.Customer.companyId === Number(customer.id)
        )
    }

    relationshipsBySupplier(supplier) {
        return this.relationships.filter(x =>
            x.Supplier &&
            x.Supplier.companyId === Number(supplier.id)
        )
    }

    relationshipsByCustomer(customer) {
        return this.relationships.filter(x =>
            x.Customer &&
            x.Customer.companyId === Number(customer.id)
        )
    }

    getSuppliersHavingRelationships(customer) {
        return this.relationships
            .filter(rel => !!rel.Supplier && !!rel.Customer && (!customer || rel.Customer.companyId === customer.id))
            .map(rel => rel.Supplier.companyId)
            .filter((value, index, self) => self.indexOf(value) === index);
    }

    getCustomersHavingRelationships(supplier) {
        return this.relationships
            .filter(rel => !!rel.Customer && !!rel.Supplier && (!supplier || rel.Supplier.companyId === supplier.id))
            .map(rel => rel.Customer.companyId)
            .filter((value, index, self) => self.indexOf(value) === index);
    }

    refreshRelationships() {
        this.relationships.fetch({ params: { partial: true } });
    }

    getRelationship(conversationId: Number): RelationshipModel {
        const conversation = globalStore.conversations.get(conversationId as any);
        return conversation && globalStore.relationships.get(conversation.relationshipId as any);
    }

    getRelationshipById(relationshipId: Number): RelationshipModel {
        return globalStore.relationships.get(relationshipId as any);
    }

    getFilteredConversationIdsByCompanyName(searchInput: string) {
        if (!searchInput) { return []; }
        const relationshipIds = this.relationships
            .filter(x => this.isCompanyNameLike(x.Customer, searchInput) || this.isCompanyNameLike(x.Supplier, searchInput))
            .map(x => x.id);
        const conversationIds = this.conversations.filter(x => relationshipIds.includes(x.relationshipId as any)).map(c => c.id);
        return conversationIds;
    }

    getSupplierParticipants() {
        return this.relationships
            .filter(rel => !!rel.Supplier)
            .map(rel => rel.Supplier);
    }

    getCustomerParticipants() {
        return this.relationships
            .filter(rel => !!rel.Customer)
            .map(rel => rel.Customer);
    }

    private isCompanyNameLike(company: Participant, searchInput: string): boolean {
        return company && company.companyName.includes(searchInput);
    }

    getInternalInvoiceStatusInfo(internalStatusId) {
        return this.internalInvoiceStatuses.find(i => i.id == internalStatusId);
    }
}

export const globalStore = (window as any)._store = Store.global = new Store();
